import React, {useState} from 'react';
import {useTranslate} from '../../../hooks';
import {
  useBillsV2,
  useCountry,
  useEnablePaymentFreeMonth,
} from '@resurs-internal/rb-app-api';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {IconBadge, VisibilityLoading} from '../../../components';
import {PaymentFreeMonthDialogRoutes} from './PaymentFreeMonthDialog';
import {BaseList} from '@resurs-internal/mui';

export const PaymentFreeMonthDialogUpdate: React.FC<{
  navigate: (key: PaymentFreeMonthDialogRoutes) => void;
}> = ({navigate}) => {
  const country = useCountry();
  const translate = useTranslate();
  const bills = useBillsV2();
  const paymentFreeMonthStatements = bills.query.data?.statements.filter(
    (e) => e.paymentFreeMonth?.available,
  );
  const enablePaymentFreeMonth = useEnablePaymentFreeMonth();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const handleCheckChange = (id: string | undefined, value: boolean) => () => {
    if (!id) return;
    setChecked((prev) => ({...prev, [id]: value}));
  };

  const dueDate =
    paymentFreeMonthStatements?.[0]?.paymentFreeMonth?.signUpDueDate;

  const onSave = async () => {
    if (!paymentFreeMonthStatements) return;
    await Promise.all(
      paymentFreeMonthStatements
        ?.filter((e) => e.paymentFreeMonth?.available)
        ?.filter((e) => e.id && Boolean(checked[e.id]))
        ?.map((e) => enablePaymentFreeMonth.mutateAsync(e)),
    );
    navigate('success');
  };

  const enabled = Object.keys(checked).length;
  return (
    <>
      <VisibilityLoading isLoading={bills.isLoading}>
        <DialogContent sx={{padding: 0}}>
          <Stack spacing={2} alignItems={'center'} p={3}>
            <IconBadge type={'font'} icon={'pause'} size={'xl'} />
            <Typography variant={'h3'} textAlign={'center'}>
              {translate('payment-free-month.dialog.subtitle')}
            </Typography>
            <Typography
              typography={'body1'}
              sx={{width: '100%', whiteSpace: 'pre-wrap'}}>
              {translate('payment-free-month.dialog.description')}
            </Typography>
            <Typography typography={'caption'} sx={{width: '100%'}}>
              {translate('payment-free-month.dialog.expiration', [dueDate])}
            </Typography>
            <Typography typography={'body1'} sx={{width: '100%'}}>
              {translate('payment-free-month.dialog.statements')}
            </Typography>
            <BaseList
              sx={{width: '100%'}}
              items={paymentFreeMonthStatements?.map((e) => {
                const disabled =
                  !e.paymentFreeMonth?.available || e.paymentFreeMonth?.active;
                const value = e.id ? checked[e.id] : undefined;
                const isChecked = e.paymentFreeMonth?.active || value;
                const title = e.designation ?? '';

                return {
                  id: `${e.id}`,
                  title: title,
                  subtitle: country.formatDate(e.dueDate),
                  icon: (
                    <Checkbox
                      inputProps={{'aria-label': `${title}`}}
                      disabled={disabled}
                      checked={isChecked}
                      onChange={handleCheckChange(e.id, !isChecked)}
                    />
                  ),
                  endAdornment: (
                    <Typography typography={'subtitle2'}>
                      {country.formatAmount(e.amountDue)}
                    </Typography>
                  ),
                };
              })}
            />
          </Stack>
          <Box
            sx={(theme) => ({
              p: 3,
              background: theme.palette.background.paper,
            })}>
            <Stack spacing={2}>
              <IconBadge
                type={'font'}
                icon={'scale-balanced'}
                inactive={true}
              />
              <Typography typography={'subtitle2'}>
                {translate('payment-free-month.dialog.terms.title')}
              </Typography>
              <Typography typography={'caption'}>
                {translate('payment-free-month.dialog.terms.text')}
              </Typography>
              <Typography typography={'subtitle2'}>
                {translate('payment-free-month.dialog.undo.title')}
              </Typography>
              <Typography typography={'caption'}>
                {translate('payment-free-month.dialog.undo.text')}
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
      </VisibilityLoading>
      <Divider />
      <DialogActions>
        <Button
          color={'secondary'}
          variant={'text'}
          onClick={() => navigate('close')}>
          {translate('common.cancel')}
        </Button>
        <Button variant={'text'} disabled={!enabled} onClick={onSave}>
          {translate('payment-free-month.dialog.button')}
        </Button>
      </DialogActions>
    </>
  );
};
